/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { useCallback, useMemo } from 'react';
import { Path as RouterPath, Location as ReactRouterLocation, useLocation } from 'react-router-dom-v5-compat';
import { Asset, AssetMediaType, AssetUtils, Channel, EntryPoint, MenuItem } from '@wit/vtv-x-foundation-lib';
import { useHistoryWithState } from './useHistoryWithState';
import { PrivateRoutePath, RoutePath, StartUpRoutePath } from '../routes/paths';
import MainSection from '../types/MainSection';
import QueryUtils from './utils/queryUtils';

export type RouterLocation = ReactRouterLocation;

export interface ParentContext {
  entryAsset?: Asset;
  entryPoint: EntryPoint;
  relatedChannel?: Channel;
}

interface AssetPath {
  asset: Asset;
  menuItemId?: number;
  entryPoint?: EntryPoint;
  state?: {
    entryAsset?: Asset;
    forceAutoPlay?: boolean;
    restartIfAvailable?: boolean;
    entryLocation?: RouterLocation;
    parentContext?: ParentContext;
  };
}

/**
 * @deprecated Will be deleted after all paths are migrated.
 */
interface CompatibilityPath {
  routePath: RoutePath;
  valuesSeries?: unknown[];
  state?: unknown;
  entryPoint?: EntryPoint;
}

type Path = AssetPath | CompatibilityPath;

function isAssetPath(path: Path): path is AssetPath {
  return (path as AssetPath).asset !== undefined;
}

/**
 * @deprecated Will be deleted after all paths are migrated.
 */
function isCompatibilityPath(path: Path): path is CompatibilityPath {
  return (path as CompatibilityPath).routePath !== undefined;
}

interface HistoryPath {
  path: string;
  state?: unknown;
}

function getRouteWithDynamicValues(path: RoutePath, valuesSeries: unknown[], basePath?: string): string {
  if (!Array.isArray(valuesSeries)) {
    return basePath || StartUpRoutePath.ROOT;
  }

  let startPath = path.toString();
  if (basePath) {
    if (startPath.startsWith('/:section')) {
      startPath = startPath.replace('/:section', basePath);
    } else if (!startPath.startsWith('/')) {
      startPath = `${basePath}/${startPath}`;
    }
  }

  const valuesSeriesCopy = [...valuesSeries];
  const pathSplited = startPath.split('/');

  return pathSplited.reduce((acc, currentValue) => {
    if (!currentValue) {
      return acc;
    }

    if (currentValue[0] !== ':') {
      return `${acc}/${currentValue}`;
    }

    const valueToAdd = valuesSeriesCopy.shift();

    if (!valueToAdd) {
      return acc;
    }

    return `${acc}/${valueToAdd}`;
  }, '');
}

function getAssetInfoRoute(asset: Asset, basePath?: string): string {
  const valuesSeries: (number | AssetMediaType)[] = [asset.mediaType, asset.id];

  return getRouteWithDynamicValues(PrivateRoutePath.PROGRAM_INFO, valuesSeries, basePath);
}

function getFolderInfoRoute(asset: Asset, basePath?: string): string {
  const valuesSeries: (number | string)[] = [asset.id];

  return getRouteWithDynamicValues(PrivateRoutePath.FOLDER_INFO, valuesSeries, basePath);
}

function createFolderPath(
  { asset, entryPoint, state, menuItemId }: AssetPath,
  location: ReactRouterLocation,
  basePath?: string
): HistoryPath {
  const path = getFolderInfoRoute(asset, basePath);
  const entryAsset = state?.entryAsset || asset;
  const entryLocation = state?.entryLocation || location;
  const parentContext = state?.parentContext;
  const searchParam = QueryUtils.encodeQueryParameters({ entryPoint, menuItemId });
  const newPath = `${path}?${searchParam}`;
  return {
    path: newPath,
    state: { entryAsset, entryLocation, parentContext },
  };
}

function createPIPath(
  { asset, entryPoint, state, menuItemId }: AssetPath,
  location: ReactRouterLocation,
  basePath?: string
): HistoryPath {
  const path = getAssetInfoRoute(asset, basePath);
  const entryAsset = state?.entryAsset || asset;
  const entryLocation = state?.entryLocation || location;
  const forceAutoPlay = state?.forceAutoPlay;
  const restartIfAvailable = state?.restartIfAvailable;

  const searchParam = QueryUtils.encodeQueryParameters({ entryPoint, menuItemId });
  const newPath = `${path}?${searchParam}`;
  return {
    path: newPath,
    state: { entryAsset, entryLocation, forceAutoPlay, restartIfAvailable },
  };
}

function createAssetPath(assetPath: AssetPath, location: ReactRouterLocation, section?: string): HistoryPath {
  const { asset } = assetPath;
  if (AssetUtils.isFolder(asset)) {
    return createFolderPath(assetPath, location, section);
  }
  return createPIPath(assetPath, location, section);
}

/**
 * @deprecated Will be deleted after all paths are migrated.
 */
function createCompatibilityPath(
  { routePath, valuesSeries, state, entryPoint }: CompatibilityPath,
  section?: string
): HistoryPath {
  let values = valuesSeries || [];
  values = section ? [section, ...values] : values;
  let path = getRouteWithDynamicValues(routePath, values || []);
  if (entryPoint) {
    const searchParam = QueryUtils.encodeQueryParameters({ entryPoint });
    path = `${path}?${searchParam}`;
  }
  return {
    path,
    state,
  };
}

export function useRouter() {
  const location = useLocation();
  const history = useHistoryWithState();

  const getSection = useCallback(
    function getSection(): MainSection {
      let { pathname } = location;
      if (pathname.startsWith('/')) {
        pathname = pathname.substring(1);
      }
      const [sectionValue] = pathname.split('/');
      if (Object.values(MainSection).indexOf(sectionValue as MainSection) >= 0) {
        return sectionValue as MainSection;
      }
      return MainSection.MY_TV;
    },
    [location]
  );

  const createPath = useCallback(
    function createPath(path: Path): HistoryPath | undefined {
      if (isAssetPath(path)) {
        return createAssetPath(path, location, getSection());
      }
      if (isCompatibilityPath(path)) {
        return createCompatibilityPath(path, getSection());
      }
      return undefined;
    },
    [getSection, location]
  );

  const push = useCallback(
    function push(path: Path) {
      const historyPath = createPath(path);
      if (historyPath) {
        history.push(historyPath.path, historyPath.state);
      }
    },
    [createPath, history]
  );

  const replace = useCallback(
    function replace(path: Path) {
      const historyPath = createPath(path);
      if (historyPath) {
        history.replace(historyPath.path, historyPath.state);
      }
    },
    [createPath, history]
  );

  const queryParams = useMemo(() => {
    const { search } = location;
    if (search) {
      return QueryUtils.decodeQueryParameters(search);
    }
    return {};
  }, [location]);

  return useMemo(() => {
    return {
      push,
      replace,
      getSection,
      goBack: history.goBack,
      queryParams,
      location,
    };
  }, [queryParams, getSection, history.goBack, push, replace, location]);
}
